import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ConfigProvider, Drawer, Menu } from "antd";
import type { MenuProps } from "antd";
import { Menu as MenuIcon, Moon, RotateCcw, Sun } from "lucide-react";
import Wordmark from "../../components/Wordmark/Wordmark";
import { TestClock } from "../../components/TestClock/TestClock";
import NotificationBell from "../../components/Notifications/NotificationBell";
import useDueCount from "../../../core/hooks/useDueCount";
import useUsuario from "../../../core/hooks/UseUsuario";
import { useModerationCountersStore } from "../../../core/store/useModerationCountersStore";
import {
  getSidebarNavGroups,
  toAntdMenuItem,
  SIDEBAR_MENU_THEME,
} from "./sidebarNavConfig";

interface AppBarProps {
  hideNav?: boolean;
}

function getInitialDark(): boolean {
  const saved = localStorage.getItem("theme");
  if (saved) return saved === "dark";
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

// Same shape as Sidebar.tsx's (unexported) initialOf — duplicated rather than
// imported so this file's only coupling to Sidebar.tsx is "same rule", not a
// shared module.
function initialOf(name?: string): string {
  const trimmed = (name ?? "").trim();
  return trimmed ? trimmed[0].toUpperCase() : "?";
}

/**
 * Mobile top bar (<768px) that replaces Sidebar+Topbar. Left to right: menu
 * button, wordmark, review button, notification bell, theme toggle, avatar.
 *
 * Deliberate divergence from frames UD407/xlGFn: both show exactly 5 controls
 * (menu, wordmark, review, theme toggle, avatar) with no bell.
 * `app-shell-pencil-fidelity`'s App Bar requirement lists those same five and
 * omits the bell — but, exactly as that same change's Topbar requirement
 * keeps the bell despite the same omission ("mounted nowhere else on
 * desktop, so removing it... would leave notifications unreachable"),
 * dropping it here would leave them unreachable below 768px with no
 * substitute: `sidebarNavConfig.tsx` has no notification entry, and
 * `e2e/notifications-mobile.spec.ts` (`forum-notifications-and-triage-
 * closure`, already merged) exercises this exact bell at this exact
 * breakpoint. Treated as the same deliberate deviation as the Topbar's,
 * which the spec just didn't spell out a second time. The menu button opens
 * the same unfiltered nav list `Sidebar`/`BottomTabBar`'s "Mais" drawer build
 * from `sidebarNavConfig`. The review button reads the same `useDueCount` as
 * the Topbar's review pill, and the avatar's initial follows Sidebar.tsx's
 * user-card rule, so neither mobile surface can disagree with desktop.
 *
 * Geometry read off UD407/xlGFn directly (not estimated): the outer row's
 * `pl-2 pr-3` is deliberately asymmetric — 8px matches the frame's menu-button
 * inset, 12px matches the frame's avatar-to-edge margin, and both stay valid
 * with a 6th control (the bell) because they're measured from the first/last
 * control, not from a count. The `gap-2` between every top-level control and
 * inside the right-hand group matches the frame's 8px rhythm everywhere
 * except that margin. Icon glyphs are kept uniform at `size={18}` across
 * menu/review/toggle rather than matching each frame icon's own asset size —
 * the frame itself isn't internally consistent there (22×22 on menu/toggle,
 * 18×18 on review), so there is no single number to chase; this follows the
 * same "structural, not asset-level" fidelity call proposal.md Assumption #1
 * already makes for colour and typeface.
 */
export default function AppBar({ hideNav }: AppBarProps) {
  const navigate = useNavigate();
  const { usuario } = useUsuario();
  const dueCount = useDueCount(!hideNav);
  const [isDark, setIsDark] = useState(getInitialDark);
  const [menuOpen, setMenuOpen] = useState(false);

  // Moderation count for the drawer's nav badge. Reads the shared store
  // rather than calling `useNotifications()` again here: that hook's
  // moderation fetch is per-instance, not deduped (see its own docstring),
  // and both `NotificationBell` below and `BottomTabBar` — always co-mounted
  // alongside this bar on mobile — already are instances firing
  // `GET /moderation/stats` into this same store. A third call here would
  // just be a third concurrent fetch for no benefit.
  const moderationStats = useModerationCountersStore((state) => state.stats);
  const moderationCount = moderationStats
    ? moderationStats.pendingComments + moderationStats.openReports
    : 0;

  useEffect(() => {
    document.documentElement.classList.toggle("dark", isDark);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleDarkMode = () => {
    const next = !isDark;
    setIsDark(next);
    document.documentElement.classList.toggle("dark", next);
    localStorage.setItem("theme", next ? "dark" : "light");
  };

  // Full nav for the signed-in profile — unlike BottomTabBar's "Mais" drawer,
  // nothing is filtered out here, since this menu is the App Bar's only
  // entry point into navigation (there is no Sidebar below the breakpoint).
  const navGroups = getSidebarNavGroups({
    profile: usuario?.profile,
    navigate,
    moderationCount,
    // Nenhuma superfície móvel monta o SidebarUserCard, então perfil e
    // saída de sessão precisam vir como itens de menu — no rail do desktop
    // é o card que os oferece.
    includeAccountItems: true,
  });
  const menuItems: NonNullable<MenuProps["items"]> = navGroups.map((group) => ({
    key: group.key,
    type: "group" as const,
    label: group.label,
    children: group.items.map(toAntdMenuItem),
  }));

  return (
    <div className="flex h-16 shrink-0 items-center justify-between gap-2 border-b border-edge bg-footer-bg pl-2 pr-3">
      {!hideNav && (
        <button
          type="button"
          onClick={() => setMenuOpen(true)}
          aria-label="Abrir menu"
          aria-expanded={menuOpen}
          title="Menu"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-input border border-edge bg-surface text-ink transition-colors hover:bg-surface-hover"
        >
          <MenuIcon size={18} strokeWidth={1.75} aria-hidden="true" />
        </button>
      )}

      <Wordmark
        width={112}
        to={hideNav ? "/planos" : "/home"}
        label={hideNav ? "Acertei — ver planos" : "Acertei — ir para a Home"}
      />

      <div className="flex-1" />

      {!hideNav && (
        <div className="flex items-center gap-2">
          {/*
            O cronômetro do simulado — ver a nota no Topbar: é ele que abre o
            `/api/ws` e redireciona para `/simulado-iniciado` quando a prova
            começa. Precisa existir nas duas larguras, porque o aluno pode
            estar esperando no celular.
          */}
          <TestClock time="00:00:00" />

          <button
            type="button"
            data-testid="appbar-review-button"
            onClick={() => navigate("/revisao")}
            title="Revisão"
            aria-label={dueCount > 0 ? `Revisão, ${dueCount} pendentes` : "Revisão"}
            className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-input border border-ok/40 bg-ok/[0.12] text-ok-ink transition-colors hover:bg-ok/[0.18] hover:text-ok-ink"
          >
            <RotateCcw size={18} strokeWidth={1.75} aria-hidden="true" />
            {dueCount > 0 && (
              <span
                data-testid="appbar-review-badge"
                aria-hidden
                className="absolute -right-1.5 -top-1.5 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-brand px-1 text-[10px] font-semibold leading-none text-brand-contrast"
              >
                {dueCount}
              </span>
            )}
          </button>

          {/* mx-1: NotificationBell's own badge overflows its icon box by
          design (`-right-2`/`-top-1.5`) — gap-2 alone would let it touch the
          review badge or the theme toggle when a count renders. */}
          <NotificationBell enabled={!hideNav} className="mx-1" />

          <button
            type="button"
            onClick={toggleDarkMode}
            aria-label={isDark ? "Ativar modo claro" : "Ativar modo escuro"}
            title={isDark ? "Modo claro" : "Modo escuro"}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-input border border-edge bg-surface text-ink transition-colors hover:bg-surface-hover"
          >
            {isDark ? (
              <Sun size={18} strokeWidth={1.75} aria-hidden="true" />
            ) : (
              <Moon size={18} strokeWidth={1.75} aria-hidden="true" />
            )}
          </button>

          <span
            data-testid="appbar-avatar"
            title={usuario?.userName || usuario?.fullName || "Usuário"}
            aria-label={`Usuário: ${usuario?.userName || usuario?.fullName || "Usuário"}`}
            className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-brand text-[13px] font-semibold text-brand-contrast"
          >
            {initialOf(usuario?.userName || usuario?.fullName)}
          </span>
        </div>
      )}

      <Drawer
        placement="left"
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        title="Menu"
        width={320}
        rootClassName="acertei-appbar-menu-drawer"
      >
        <ConfigProvider theme={SIDEBAR_MENU_THEME}>
          <Menu
            mode="inline"
            theme="light"
            selectable={false}
            onClick={() => setMenuOpen(false)}
            items={menuItems}
            style={{ background: "transparent", borderInlineEnd: 0 }}
          />
        </ConfigProvider>
      </Drawer>
    </div>
  );
}
